import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "../../custom/sub-header/sub-header";
import {
  COUPONS,
  COUPON_TABS,
  COUPON_EMPTY,
} from "../../../constant/coupon-content";
import "./coupon-page-style.scss";

//優惠券
//
//  ⚠️ 後端還沒有優惠券模型，資料來源是 constant/coupon-content 的空陣列。
//  所以這一頁現在**一定是空狀態** —— 那是預期中的樣子，不是壞掉。
//  接上 API 時只要把那支常數換成回來的清單，這裡不用動。
//
//  —— 為什麼做成票券的形狀而不是一般卡片 ——
//  金額是這張券唯一要一眼看到的東西。左右切開、中間打一條撕線，
//  視線自然落在左邊那個數字上；排成一般卡片的話金額會變成內文的一部分。

//到期日。只顯示日期不顯示時間 ——
//  「11/30 23:59 前」在卡片上要折行，而分秒對使用者的決定沒有影響。
const fmtExpire = (ms) => {
  if (!ms) return "無使用期限";
  const d = new Date(ms);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} 前使用`;
};

//門檻。0 要講成「無門檻」而不是「滿 0 元可用」。
const fmtMin = (min) => (min > 0 ? `單筆滿 ${min} 元可用` : "無消費門檻");

const CouponPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("usable");

  //  分頁的數字要先算好：切到空分頁時才知道是「真的沒有」
  //  還是「還沒載到」。資料是同步的常數，所以這裡不會有載入中。
  const grouped = useMemo(() => {
    const g = { usable: [], used: [], expired: [] };
    for (const c of COUPONS) (g[c.state] || g.expired).push(c);
    return g;
  }, []);

  const shown = grouped[tab] || [];
  const empty = COUPON_EMPTY[tab] || COUPON_EMPTY.usable;

  return (
    <main className="cpn">
      <SubHeader title="優惠券" fallback="/account" />

      <nav className="cpn__tabs">
        {COUPON_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`cpn__tab${tab === t.key ? " is-on" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {grouped[t.key].length > 0 && (
              <span className="cpn__tab-n">{grouped[t.key].length}</span>
            )}
          </button>
        ))}
      </nav>

      {shown.length === 0 ? (
        <div className="cpn__empty">
          {/* 空的時候畫一張虛線的空券 —— 比一行字更說得清楚這一頁是放什麼的 */}
          <div className="cpn__ghost" aria-hidden="true">
            <span className="cpn__ghost-mark">券</span>
          </div>
          <p className="cpn__empty-title">{empty.title}</p>
          {empty.text && <p className="cpn__empty-text">{empty.text}</p>}
          {empty.cta && (
            <button
              type="button"
              className="cpn__empty-btn"
              onClick={() => navigate("/")}
            >
              {empty.cta}
            </button>
          )}
        </div>
      ) : (
        <ul className="cpn__list">
          {shown.map((c) => (
            <li
              key={c.id}
              className={`cpn__card${c.state === "usable" ? "" : " is-off"}`}
            >
              {/* 左聯：金額。percent 的單位在數字後面，amount 在前面 ——
                  「折 100 元」與「85 折」是兩種唸法，共用一種排法會有一邊很怪 */}
              <div className="cpn__stub">
                {c.kind === "percent" ? (
                  <>
                    <span className="cpn__value">{c.value}</span>
                    <span className="cpn__unit">折</span>
                  </>
                ) : (
                  <>
                    <span className="cpn__unit">NT$</span>
                    <span className="cpn__value">{c.value}</span>
                  </>
                )}
              </div>

              <div className="cpn__body">
                <p className="cpn__name">{c.name}</p>
                <p className="cpn__rule">{fmtMin(c.min)}</p>
                {c.scope && <p className="cpn__rule">{c.scope}</p>}
                <p className="cpn__expire">{fmtExpire(c.expireAt)}</p>
              </div>

              {/* 已用／過期蓋一枚章。用文字不用圖：兩個字就說完了，
                  而且放大縮小都不會糊 */}
              {c.state !== "usable" && (
                <span className="cpn__stamp">
                  {c.state === "used" ? "已使用" : "已過期"}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="cpn__note">
        優惠券於結帳時套用，一筆訂單限用一張，不與其他折扣併用。
      </p>
    </main>
  );
};

export default CouponPage;
